



























































































































































import {
  computed,
  defineComponent,
  reactive,
  toRefs,
  ref,
  inject,
} from '@vue/composition-api'
import CreateCategory from '@/views/dashboard/components/category/CreateCategory.vue'
import EditCategory from '@/views/dashboard/components/category/EditCategory.vue'
import draggable from 'vuedraggable'
import { deleteCategories, reorderCategories } from '@/shared/api'
import { Textbook, Category } from '@/shared/types'
import { reorderArray, sortByOrder } from '@/shared/utils'
import { useStore as useUserStore } from '@/shared/state-management/userStore'
import {
  CREATE_CATEGORY,
  EDIT_CATEGORY,
  DELETE_CATEGORY,
  REORDER_CATEGORIES,
} from '@/shared/user/permissions'
import { alertSymbol } from '@/shared/state-management/symbols'

export default defineComponent({
  name: 'Categories',
  components: {
    CreateCategory,
    EditCategory,
    draggable,
  },
  props: {
    textbook: Object,
    loadingTextbooks: Boolean,
  },
  setup(props, { root, emit }) {
    const { hasPermissionTo } = useUserStore().getters
    const createCategoryDialogOpen = ref(false)
    const editCategoryDialogOpen = ref(false)
    const search = ref('')
    const selectedCategories = ref([])
    const selectedCategory = ref(null as Category)
    const reorderingCategories = ref(false)
    const draggedId = ref(null)
    const tempArray = ref(null)
    const $alert = inject(alertSymbol) as Function
    const deletingCategory = ref(false)

    const data = reactive({
      headers: [
        {
          text: root.$t('categories.name'),
          value: 'name',
        },
        {
          text: root.$t('actions'),
          value: 'actions',
        },
      ],
      search: undefined,
    })

    const loadingTable = computed(
      () =>
        props.loadingTextbooks ||
        reorderingCategories.value ||
        deletingCategory.value
    )

    const categories = computed<Array<Category>>(() => {
      if (!props.textbook) {
        return []
      }
      return tempArray.value ?? sortByOrder(props.textbook.categories)
    })

    const editItem = (item: Category) => {
      selectedCategory.value = item
      editCategoryDialogOpen.value = true
    }

    const deleteItem = async (category: Category) => {
      await $alert({
        type: 'confirm',
        text: root.$t('categories.confirmDelete', { category: category.name }),
      })
      deletingCategory.value = true
      try {
        const ids = await deleteCategories(
          props.textbook.id,
          [category.id]
        )
        emit('categories-deleted', ids)
        deletingCategory.value = false
      } catch (e) {
        deletingCategory.value = false
        throw e
      }
    }

    const deleteSelected = async () => {
      await $alert({
        type: 'confirm',
        text: root.$t('categories.confirmDeleteSelected', {
          number: selectedCategories.value.length,
        }),
      })
      const categoryIds = selectedCategories.value.map(
        (category) => category.id
      )
      deletingCategory.value = true
      try {
        const ids = await deleteCategories(props.textbook.id, categoryIds)
        selectedCategories.value = []
        emit('categories-deleted', ids)
        deletingCategory.value = false
      } catch (e) {
        deletingCategory.value = false
        throw e
      }
    }

    const onReorderCategories = async (oldIndex: number, newIndex: number) => {
      if (reorderingCategories.value || oldIndex === newIndex) {
        return
      }

      reorderingCategories.value = true
      try {
        const newArray = reorderArray(categories.value, oldIndex, newIndex).map(
          (el: Category, index: number) => ({
            ...el,
            order: index,
          })
        )

        tempArray.value = [...newArray]

        const order = newArray.map((el: Category, index: number) => ({
          id: el.id,
          order: '' + index,
        }))

        // textbookIssuePageMediaElements.value = newArray
        const updatedTextbook = await reorderCategories(
          props.textbook.id,
          order
        )
        emit('categories-reordered', updatedTextbook)
        reorderingCategories.value = false
        tempArray.value = null
      } catch (e) {
        tempArray.value = null
        reorderingCategories.value = false
        throw e
      }
      return true
    }

    const onCategoryCreated = (textbook: Textbook) => {
      emit('category-created', textbook)
    }

    const onCategoryEdited = (textbook: Textbook) => {
      emit('category-edited', textbook)
    }

    return {
      ...toRefs(data),
      categories,
      search,
      onCategoryCreated,
      onCategoryEdited,
      createCategoryDialogOpen,
      editCategoryDialogOpen,
      selectedCategories,
      selectedCategory,
      editItem,
      deleteItem,
      deleteSelected,
      onReorderCategories,
      reorderingCategories,
      loadingTable,
      draggedId,
      hasPermissionTo,
      CREATE_CATEGORY,
      EDIT_CATEGORY,
      DELETE_CATEGORY,
      REORDER_CATEGORIES,
    }
  },
})
